/**
 * BeslisNamensKlantDialog — office beslist namens de klant op een
 * Wacht_klant-risico (tokenloos primitief, plan 2026-07-21). Keuze
 * Akkoord / Niet akkoord / Alternatief + verplichte reden (≥10 tekens).
 * Het besluit wordt met naam en reden vastgelegd in het audit-spoor;
 * de snapshot claimt expliciet geen klant-consent.
 */
import { useState } from 'react';
import { Button } from '../ui';
import { beslisNamensKlant, type NamensKlantResult } from '../../lib/risico';

const REDEN_MIN = 10;

const ALTERNATIEF_OPTIES = [
  { code: 'WAS-PRIM-ONBEHANDELD-RETOUR', label: 'Onbehandeld retour sturen' },
  { code: 'WAS-PRIM-ONBEHANDELD-ZELFHALEN', label: 'Onbehandeld zelf laten ophalen' },
  { code: 'WAS-PRIM-VLOERKLEED-AFVOEREN', label: 'Vloerkleed afvoeren' },
] as const;

type Keuze = 'akkoord' | 'niet_akkoord' | 'alternatief';

interface Props {
  wasserijItemId: string;
  kleedNaam: string;
  klantNaam: string | null;
  onSuccess: (wasserijItemId: string) => void;
  onClose: () => void;
}

export function BeslisNamensKlantDialog({
  wasserijItemId,
  kleedNaam,
  klantNaam,
  onSuccess,
  onClose,
}: Props) {
  const [keuze, setKeuze] = useState<Keuze>('akkoord');
  const [productCode, setProductCode] = useState<string>(ALTERNATIEF_OPTIES[0].code);
  const [reden, setReden] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);

  const redenOk = reden.trim().length >= REDEN_MIN;

  const handleSubmit = async () => {
    if (!redenOk || busy) return;
    setBusy(true);
    setError(null);
    const result: NamensKlantResult = await beslisNamensKlant({
      wasserijItemId,
      ...(keuze === 'alternatief' ? { productCode } : { akkoord: keuze === 'akkoord' }),
      reden: reden.trim(),
    });
    setBusy(false);
    if (!result.success) {
      setError(result.error || 'Namens-klant beslissen mislukt');
      return;
    }
    if (result.warning) {
      // Besluit staat (domein), SF-heling volgt — wel even laten zien.
      setWarning(result.warning);
      setTimeout(() => onSuccess(wasserijItemId), 2500);
      return;
    }
    onSuccess(wasserijItemId);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Beslis namens klant</h2>
          <p className="text-sm text-gray-600 mt-1">
            {kleedNaam}
            {klantNaam ? ` van ${klantNaam}` : ''}
          </p>
        </div>

        <div className="space-y-2">
          {(
            [
              { value: 'akkoord', label: 'Akkoord', hint: 'Klant gaat (buitenom) akkoord met reiniging op eigen risico' },
              { value: 'niet_akkoord', label: 'Niet akkoord', hint: 'Klant wil niet dat we reinigen' },
              { value: 'alternatief', label: 'Alternatief', hint: 'Onbehandeld retour, zelf ophalen of afvoeren' },
            ] as Array<{ value: Keuze; label: string; hint: string }>
          ).map((opt) => (
            <label
              key={opt.value}
              className={`flex items-start gap-3 rounded-md border p-3 cursor-pointer ${
                keuze === opt.value ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200'
              }`}
            >
              <input
                type="radio"
                name="namens-klant-keuze"
                checked={keuze === opt.value}
                onChange={() => setKeuze(opt.value)}
                className="mt-1"
              />
              <span>
                <span className="block text-sm font-medium text-gray-900">{opt.label}</span>
                <span className="block text-xs text-gray-500">{opt.hint}</span>
              </span>
            </label>
          ))}
        </div>

        {keuze === 'alternatief' && (
          <select
            value={productCode}
            onChange={(e) => setProductCode(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
          >
            {ALTERNATIEF_OPTIES.map((o) => (
              <option key={o.code} value={o.code}>
                {o.label}
              </option>
            ))}
          </select>
        )}

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Reden (verplicht) - bv. "klant gaf telefonisch akkoord op 21-07" of
            "geen reactie na 2 weken, link verlopen"
          </label>
          <textarea
            value={reden}
            onChange={(e) => setReden(e.target.value)}
            rows={3}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
            disabled={busy}
          />
          <p className={`mt-1 text-xs ${redenOk ? 'text-gray-400' : 'text-amber-700'}`}>
            {reden.trim().length}/{REDEN_MIN} tekens minimaal
          </p>
        </div>

        <p className="rounded-md bg-amber-50 border border-amber-200 p-2 text-xs text-amber-800">
          Je beslist namens de klant; dit wordt vastgelegd met jouw naam en reden.
        </p>

        {error && (
          <p className="rounded-md border border-red-200 bg-red-50 p-2 text-sm text-red-700">{error}</p>
        )}
        {warning && (
          <p className="rounded-md border border-amber-200 bg-amber-50 p-2 text-sm text-amber-800">
            Besluit geregistreerd. {warning}
          </p>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Annuleren
          </Button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!redenOk || busy}
            className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            {busy ? 'Bezig…' : 'Beslis namens klant'}
          </button>
        </div>
      </div>
    </div>
  );
}
